// ---------------------------------------------------------------------------
// Everything you'd want to edit lives here.
// ---------------------------------------------------------------------------

export const SITE = {
    name: 'SofaTech',
    // Bump on any change that moves the ground: a new island, a moved one, a
    // different seed. A saved berth from an older world is thrown away rather
    // than checked, because "is this spot still water?" is a guess and "was
    // this saved for the world we are in?" is a fact.
    worldVersion: 4,
    tagline: 'We know better. From the sofa.',
    // Deterministic world. Change the seed, get a different polder.
    seed: 20240418,
};

// The two windmills of the Zaanse Schans. Each mill IS a link.
// "accent" tints the mill's shutters, door and cap trim.
// "bank" places it along the river; "offset" is how far back from the water.
export const MILLS = [
    {
        id: 'jonge-schaap',
        name: 'Het Jonge Schaap',
        accent: 0x8a6a3f,
        bank: 0.28,         // position along the canal, 0..1
        // Right down on the water: a sawmill takes its logs off the river, so
        // it stands closer to it than the others do.
        offset: -11,        // voxels perpendicular from the canal centre
        facing: -0.55,      // radians the cap/sails are turned to
        spin: 0.30,         // base sail speed (rad/s)
        card: {
            title: 'Het Jonge Schaap',
            icon: 'mill-icon',
            text: 'A sawmill, and a rebuilt one: the original was pulled down '
                + 'in 1942 and this replica was raised between 2005 and 2007 '
                + 'from photographs and a set of seventeenth-century drawings. '
                + 'It saws logs to this day \u2014 the Zaan ran on timber long '
                + 'before it ran on tourists.',
        },
    },
    {
        id: 'huisman',
        name: 'De Huisman',
        accent: 0xd9b23a,
        bank: 0.78,
        // De Huisman stands right on the Zaan too, with the water under its
        // stage — it is a small mill on the bank, not one set back in a field.
        offset: -11,
        facing: 0.75,
        spin: 0.24,
        card: {
            title: 'De Huisman',
            icon: 'mill-icon',
            text: 'Built around 1786, and it has ground whatever paid: snuff '
                + 'first, then timber, and mustard since. The mustard is still '
                + 'made here and still sold at the door.',
        },
    },
    {
        id: 'de-kat',
        name: 'De Kat',
        // Octagonal, and standing on its own storehouse — see the card.
        smock: true,
        // The dye mill grinds colour, so it wears some.
        accent: 0x9c4a6e,
        bank: 0.50,
        offset: -18,
        facing: 0.15,
        spin: 0.42,         // the busiest mill on the river
        card: {
            title: 'De Kat',
            icon: 'mill-icon',
            text: 'The only working paint-dye windmill left in the world. It '
                + 'grinds chalk, wood and stone into pigment the way it always '
                + 'did, and sells it by the jar to painters who want colour '
                + 'that no factory makes any more.',
        },
    },
];

// Not everything worth linking to deserves a windmill. The dashboard is a
// private tool, so it gets the lockkeeper's house on the near bank instead —
// small, lit from within, across the water from the mills.
export const HOUSES = [
    {
        id: 'linkedin',
        name: 'LinkedIn',
        url: 'https://www.linkedin.com/in/vladvlasov/',
        accent: 0x0a66c2,
        bank: 0.24,         // position along the canal, 0..1
        offset: 14,         // positive: the near bank, across from the mills
        width: 8, depth: 6, wallHeight: 5,
    },
    {
        id: 'github',
        name: 'GitHub',
        url: 'https://github.com/vladvlasov256',
        accent: 0x8957e5,
        bank: 0.42,
        offset: 16,
        width: 8, depth: 6, wallHeight: 5,
    },
    {
        id: '31labs',
        name: '31labs.io',
        // 31Labs brands in pure black and white, so this one gets ink boards.
        accent: 0x14171c,
        url: 'https://31labs.io/',
        bank: 0.60,
        offset: 14,
        width: 8, depth: 6, wallHeight: 5,
    },
    {
        id: 'umami',
        name: 'Analytics',
        url: 'https://umami.sofatech.xyz',
        accent: 0x2f9e6f,
        bank: 0.78,
        offset: 15,
        width: 9, depth: 7, wallHeight: 5,
    },
];

// The wind. Constant, because a website that makes you wait for a shift is not
// a fun website — and because the mills all face the same way anyway.
export const WIND = {
    // Live: set every frame from the shared clock, so every boat on the water
    // is sailing the same weather. Read it, never assume it.
    direction: -2.2,    // radians the wind blows TOWARD, in world XZ
    base: -2.2,         // what it backs and veers around
    swing: 1.15,        // how far either side of that, in radians
    period: 340,        // seconds for a full turn of the weather
    strength: 3.0,      // world units per second with the wind astern
    turnRate: 0.70,     // radians per second on the tiller
    offing: 132,        // world units out before the tide sets you back
    setback: 5.0,       // how hard it sets, in world units per second
};

// The sea room. The island's coast is roughly 23 world units from the middle,
// and the voxel grid runs out at 25.6 — so the shoal ring lies well beyond the
// map and cannot be made of terrain. The water shader draws it and the sailing
// model treats it as ground; a ring of marker posts makes it something you can
// see coming rather than an invisible wall.
export const SEA = {
    // 150 is deliberate: the water plane has a floor of 420 units and only
    // starts growing past a ring of ~161, so the sea room is nearly four times
    // the old one for no extra geometry at all.
    shoalRadius: 150,   // world units from the middle of the island
    shoalPosts: 24,     // marks around the ring
    spawnFraction: 0.34, // where a visitor's boat appears, as a share of the ring
};

// Other visitors. Every tab looking at the site is one boat; the relay holds
// who is on the water right now and nothing else. If it is unreachable the
// site is simply single-handed — never let this break the page.
export const FLEET = {
    enabled: true,
    path: '/fleet',     // proxied by Caddy on the same host
    url: '',            // force a relay address; otherwise worked out from the page
    devPort: 8787,      // where `node fleet.js` listens on a dev machine
    // Must match the server's list, in order: the flag arrives as a colour and
    // is looked up here to find which palette entry to fly.
    flags: [
        0xe6483c, 0xf2a93b, 0x4bb96b, 0x3fa9d6, 0x8b6bd9, 0xe07ab5,
        0xd9d24a, 0x35c2ac, 0xf07a3c, 0x7f9cf0, 0xc0553f, 0x6fbf3f,
    ],
};

// Music. Off until asked for, and only fetched then — see src/ui/music.js.
//
// Two tracks, cross-faded by the same sun that lights the world: piano while
// it is up, something darker once it is down. Both are CC-BY 4.0 and the
// credit is not optional — it is the licence.
export const MUSIC = {
    day: 'assets/music/glow.mp3',
    night: 'assets/music/the-long-dark.mp3',
    volume: 0.34,
    fade: 1.1,          // how fast a track hands over, in units per second
    // Pressing mute should feel like mute. A handover can take its time; a
    // visitor who wants silence wants it now, not in three seconds.
    fadeOut: 6.0,
    credit: "Music by Scott Buckley — CC-BY 4.0",
    creditUrl: 'https://www.scottbuckley.com.au',
};

// Herring. Schools come and go on their own; finding one is the whole game.
export const HERRING = {
    maxSchools: 2,          // never more on the water at once
    perMinute: 2.5,         // roughly how often a new one shows up
    radius: 8,              // how close counts as "over the school"
    sizeMin: 8,             // fish in a school
    sizeMax: 25,
    fishSecondsMin: 10,     // how long that school takes to work out
    fishSecondsMax: 50,
    // With only two slots, how long a school lingers is what really sets the
    // rate: two schools over an average life of ~45s is about 2.5 a minute.
    // Longer lives and the cap throttles spawning no matter what perMinute says.
    lifeMin: 64,            // how long an untouched school stays up
    lifeMax: 116,
    spawnNear: 34,          // spawn between these radii, so never underfoot
    spawnFar: 120,
};

/**
 * Where the wind is at a given moment. Derived from the clock rather than
 * broadcast, so every client works out the same weather without the relay
 * having to say a word about it — and two harmonics rather than one, or it
 * backs and veers on an obvious metronome.
 */
export function windAt(nowMs) {
    const t = nowMs / 1000;
    const a = Math.sin((t / WIND.period) * Math.PI * 2);
    const b = Math.sin((t / (WIND.period * 0.37)) * Math.PI * 2 + 1.7);
    return WIND.base + (a * 0.78 + b * 0.22) * WIND.swing;
}

// What each Zaanse Schans mill sends down to its wharf. All three are real:
// Het Jonge Schaap saws, De Huisman grinds mustard, and De Kat is the only
// working paint-dye windmill left on earth.
export const GOODS = [
    { id: 'timber', mill: 'jonge-schaap', name: 'Planks', from: 'Het Jonge Schaap' },
    { id: 'mustard', mill: 'huisman', name: 'Mustard', from: 'De Huisman' },
    { id: 'pigment', mill: 'de-kat', name: 'Pigment', from: 'De Kat' },
];

// Buying goods. No quantity dialog: one press takes a lot of ten, capped by
// what the mill still has, what the hold will take and what you can pay for.
// Press again to take another.
export const TRADE = {
    lot: 10,
    capacity: 30,       // how many crates the hold will carry
};

// Where a boat counts as lying at a mill wharf. Hand-drawn boxes over the
// water off each pier, the same as the ports.
export const WHARVES = [
    { id: 'timber', box: { x0: -87, x1: -73, z0: -26, z1: -12 } },
    { id: 'mustard', box: { x0: -55.3, x1: -41.3, z0: 3.1, z1: 17.1 } },
    { id: 'pigment', box: { x0: -64.9, x1: -50.9, z0: -24.7, z1: -10.7 } },
];

// The ferry between Volendam and Marken — the crossing that has run there for
// a century. People gather on a quay whether or not anyone comes for them, and
// once landed they go home rather than joining the queue on the other side:
// there is no rowing the same dozen back and forth for a wage.
export const FERRY = {
    capacity: 8,        // how many the boat can carry
    fare: 1,            // guilders a head
    queueMax: 12,       // how many will stand on a quay before giving up
    everySeconds: 7,    // how often one more turns up
    // How far off the quay's own line a boat may lie. The quay is a line —
    // the stone at Marken, the jetty at Volendam — so this is the width of the
    // strip of water running alongside it, not a circle round one end.
    radius: 5,
};

// Where a boat counts as alongside: two boxes, drawn by hand over the water
// beside each quay. Not derived from anything — the shapes of a harbour mouth
// and a stone stub are not the same shape, and no radius or offset describes
// both. Printed the water as a grid, then drew the rectangle.
//
// Tied to the world layout, so they move when the islands do. SITE.worldVersion
// is the guard.
export const PORTS = [
    // Volendam is an arc, not a box: a band of water hugging the quay along
    // the landward side of the basin, so you have to come alongside the stall
    // rather than sit in the middle of the harbour. The stall stands at
    // bearing -2.3 from the basin's centre, eight units out.
    {
        id: 'volendam',
        // r0 is 0 on purpose: an inner radius leaves a hole in the middle
        // of the harbour, and the middle of the harbour is exactly where a
        // boat comes to rest. A sector, not a ring.
        arc: { x: 50.2, z: -1.4, r0: 0, r1: 8.5, at: -2.3, spread: 2.2 },
    },
    // The water pressed against Marken's stone landing. The stub runs west
    // from the shore, so this is a short strip beside it — the old box was a
    // slab reaching eight units out to sea, which is not alongside anything.
    { id: 'marken', box: { x0: 63.5, x1: 71, z0: 10.5, z1: 20 } },
];

// The fish market on the shore.
export const MARKET = {
    radius: 13,         // how close to the jetty counts as alongside
    // What a herring fetches when nobody has been flooding the market. The
    // live price comes from the relay — one market, one price — and this is
    // only the fallback for sailing alone.
    price: 3.0,
};

// Landmarks worth reading about. Not links and not destinations — things you
// sail past that turn out to have something to say for themselves.
export const LANDMARKS = {
    volendam: {
        title: 'Sint-Vincentiuskerk',
        icon: 'church-icon',
        text: 'Built in 1860 to Theo Molkenboer\u2019s neo-Gothic design, and '
            + 'twice enlarged since \u2014 the transept and choir in 1882, more '
            + 'again in 1907. Its Adema-Schreurs organ carries painted panels of '
            + 'dancing mice, which gave enough offence in 1961 to set off '
            + 'disturbances in the village. The opera choir still sings here '
            + 'every year.',
    },
    marken: {
        title: 'Paard van Marken',
        icon: 'lighthouse-icon',
        text: 'The Horse of Marken has stood at the eastern tip of the island '
            + 'since 1839, fifteen and a half metres of it, on the site of a '
            + 'cruder light that was already there in the 1700s. Its signal is '
            + 'occulting white every eight seconds \u2014 lit almost all the '
            + 'time, with one brief eclipse, which is the opposite of a flash. '
            + 'Sailors know it by that dark beat.',
    },
};

// Things worth knowing about herring, shown on the card when a school is
// clicked. Kept short enough to read from the tiller.
export const HERRING_FACTS = [
    'A herring school can run to hundreds of millions of fish — among the largest gatherings of vertebrates on Earth.',
    'Herring talk by farting. Bubbles vented from the swim bladder make a rapid clicking only other herring can hear.',
    'Those bursts run from 7 to 65 clicks at a time, and are thought to hold the school together after dark.',
    'A herring\u2019s swim bladder connects to its gut at one end and its inner ear at the other, so it hears through it.',
    'That gut-to-swim-bladder plumbing is found in herring and almost nowhere else among fish.',
    'Swedish naval sonar once mistook schools of farting herring for Soviet submarines.',
    'Herring hear far better than most fish, well up into frequencies we would call ultrasound.',
    'A single female can lay tens of thousands of eggs, which sink and stick to the seabed rather than drift.',
    'Dutch herring is eaten raw as haring, cured in a light brine — the first barrel of the season is auctioned for charity.',
    'Curing herring in barrels kept northern Europe fed through winter and paid for a good deal of Amsterdam.',
];

// Where the islands sit in the sea. Hardcoded on purpose: the layout is the
// one part of the map worth designing by hand — the terrain itself is still
// generated from the seed, so nothing has to be shipped as data.
//
// The positions come from the real ones. Volendam is 17 km east of the Zaanse
// Schans and a little north; Marken is 19.5 km east and a little south, so
// Volendam sits north of it. The two are under 5 km apart, which is why there
// has been a ferry between them for a century.
//
// Note the sign: +Z is south here, not north. The camera looks along -Z, so a
// place north of another has the smaller z — getting that backwards mirrors
// the whole map, which is exactly what happened the first time.
export const ISLANDS = [
    {
        id: 'zaanse-schans',
        name: 'Zaanse Schans',
        x: -70, z: 0,
        size: 128,          // grid columns a side — 51 world units
        canal: true,        // the Zaan runs through it
        built: true,
    },
    {
        id: 'volendam',
        name: 'Volendam',
        x: 35, z: -15,      // north of Marken, as on the map
        size: 128,
        canal: false,
        built: true,
    },
    {
        id: 'marken',
        name: 'Marken',
        x: 80, z: 25,       // south-east of Volendam, further out
        size: 88,           // smaller than Volendam, as in life
        canal: false,
        built: true,
    },
];

// World dimensions, in voxels.
export const WORLD = {
    size: 128,          // grid is size x size columns
    height: 40,         // vertical voxel budget
    voxelSize: 0.4,     // world units per voxel
    seaLevel: 6,        // voxel y of the waterline
};

export const RENDER = {
    dayLength: 600,     // seconds for a full 24h cycle (10 real minutes)
    startHour: 9.5,     // where the clock starts
    maxPixelRatio: 2,
};
